import {
  ISchema,
  ISchemaNode,
  Schema,
  forEachSchemaNode,
} from '@framework/validation';
import {
  ApplicantDataKey,
  ApplicantDataTypes,
  IApplicantData,
} from './ApplicantDataTypes';
import {
  CompanyApplicantDataKey,
  CompanyApplicantDataTypes,
  ICompanyApplicantData,
} from './CompanyApplicantData';

function getArrayPath(node: ISchemaNode, widgetId: string): string {
  return node.inArrayPath! + '.' + node.inArrayIndex! + '.' + widgetId;
}

function manageApplicantData(props: {
  isArray: boolean;
  node: ISchemaNode;
  widgetId: string;
  applicantsData: Record<string, IApplicantData>;
  applicantDataCode: ApplicantDataKey;
  companyApplicantsData: Record<string, ICompanyApplicantData>;
}) {
  const {
    isArray,
    node,
    widgetId,
    applicantsData,
    applicantDataCode,
    companyApplicantsData,
  } = props;

  if (isArray) {
    // Se siamo in un array, decodifichiamo il percorso "comune" del campo
    // e lo usiamo come chiave dell'oggetto.
    // In questo modo possiamo gestire i dati del titolare in un array
    // "raggruppando" gli attributi nello stesso figlio della lista.
    const commonPath = getArrayPath(node, widgetId);

    // Costruisco l'array delle possibili chiavi del widget azienda
    // risalendo dai vari array parents
    const companyKey =
      node.inArrayParents
        ?.map(p => p + '.' + widgetId)
        // Aggiungo il caso root (id = widgetId)
        .concat(widgetId) ?? [];

    const companyDataKey = Object.keys(companyApplicantsData).find(k =>
      companyKey?.includes(k),
    );

    const companyData = companyDataKey
      ? companyApplicantsData[companyDataKey]
      : undefined;

    addApplicantData(
      applicantsData,
      commonPath,
      applicantDataCode,
      node.value,
      companyData,
      widgetId,
    );
  } else {
    const companyData = companyApplicantsData[widgetId];

    // Se non siamo in un array, esportiamo il valore direttamente
    addApplicantData(
      applicantsData,
      widgetId,
      applicantDataCode,
      node.value,
      companyData,
      widgetId,
    );
  }
}

function manageCompanyApplicantData(props: {
  isArray: boolean;
  node: ISchemaNode;
  widgetId: string;
  companyApplicantsData: Record<string, ICompanyApplicantData>;
  applicantDataCode: CompanyApplicantDataKey;
}) {
  const { isArray, node, widgetId, companyApplicantsData, applicantDataCode } =
    props;
  if (isArray) {
    const commonPath = getArrayPath(node, widgetId);

    addCompanyApplicantData(
      companyApplicantsData,
      commonPath,
      applicantDataCode,
      node.value,
    );
  } else {
    addCompanyApplicantData(
      companyApplicantsData,
      widgetId,
      applicantDataCode,
      node.value,
    );
  }
}

/**
 * Esporta i valori di un modulo in base alla direttiva `tableSave` dei campi.
 *
 * /!\ I titolari multipli sono supportati solamente a livello di Lista
 * Ripetibile
 */
export function exportApplicantDataDirective(
  schema: ISchema<any>,
  options: { value: any; context?: any },
) {
  const applicantsData = {} as Record<string, IApplicantData>;
  const companyApplicantsData = {} as Record<string, ICompanyApplicantData>;

  // Andiamo a costruire l'oggetto con l'elenco di tutte le aziende configurate
  // e compilate nel form.
  forEachSchemaNode(schema, options, node => {
    if (node.schema instanceof Schema) {
      const applicantDataCode = node.schema.meta?.()
        ?.directiveApplicantData as ApplicantDataKey;

      // Se NON è un campo del titolare di tipo azienda, non fare nulla
      if (!applicantDataCode) return;
      if (!CompanyApplicantDataTypes.isCompanyDataCode(applicantDataCode))
        return;

      // Recupero l'ID del widget azienda
      const companyWidgetId: string = node.schema.meta?.()?.parentCompany ?? 'root'; // prettier-ignore
      const isArray = node.inArrayPath != null;

      manageCompanyApplicantData({
        isArray,
        node,
        widgetId: companyWidgetId,
        companyApplicantsData,
        applicantDataCode,
      });
    }
  });

  // Andiamo a costruire l'oggetto con l'elenco di tutti i titolari
  // configurati e compilati nel form, collegandoli quando necessario
  // all'azienda.
  forEachSchemaNode(schema, options, node => {
    if (node.schema instanceof Schema) {
      const applicantDataCode = node.schema.meta?.()
        ?.directiveApplicantData as ApplicantDataKey;

      // Se NON è un campo del titolare, non fare nulla
      if (!applicantDataCode) return;
      if (!ApplicantDataTypes.isApplicantDataCode(applicantDataCode)) return;

      // Recupero l'ID del widget azienda
      const companyWidgetId: string = node.schema.meta?.()?.parentCompany ?? 'root'; // prettier-ignore
      const isArray = node.inArrayPath != null;

      manageApplicantData({
        isArray,
        node,
        widgetId: companyWidgetId,
        applicantsData,
        applicantDataCode,
        companyApplicantsData,
      });
    }
  });

  return Object.values(applicantsData);
}

function addApplicantData(
  applicantsData: Record<string, IApplicantData>,
  uniqueKey: string,
  dataKey: ApplicantDataKey,
  value: any,
  companyData: ICompanyApplicantData | undefined,
  widgetId: string,
) {
  // Se il valore è nullo, non fare nulla
  if (value == null) return;

  // Se non esiste ancora l'oggetto per il titolare, lo creiamo
  if (!applicantsData[uniqueKey]) {
    applicantsData[uniqueKey] = {} as IApplicantData;
  }
  // Se non esiste ancora l'attributo, lo creiamo
  if (!applicantsData[uniqueKey][dataKey]) {
    applicantsData[uniqueKey][dataKey] = value;
  }
  if (!applicantsData[uniqueKey].company) {
    applicantsData[uniqueKey].company = companyData;
  }
}

function addCompanyApplicantData(
  companyApplicantData: Record<string, ICompanyApplicantData>,
  companyWidgetId: string,
  dataKey: CompanyApplicantDataKey,
  value: any,
) {
  // Se il valore è nullo, non fare nulla
  if (value == null) return;

  if (!companyApplicantData[companyWidgetId]) {
    companyApplicantData[companyWidgetId] = {} as ICompanyApplicantData;
  }
  if (!companyApplicantData[companyWidgetId][dataKey]) {
    companyApplicantData[companyWidgetId][dataKey] = value;
  }
}
